import React, { useState } from 'react';
import { post, put, deleteRequest } from 'helpers/api';
import { Modal } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash } from '@fortawesome/pro-solid-svg-icons';
import Secondary from '../../../shared/button/secondary';
import Danger from '../../../shared/button/danger';
import JobTitleModal from './job_title_modal';
import ErrorAlert from './error_alert';

export default function JobTitles({ jobTitles, jobTitleableId, jobTitleableType }: JobTitlesProps) {
  const [currentJobTitles, setCurrentJobTitles] = useState(jobTitles);
  const [modalOpen, setModalOpen] = useState(false);
  const [jobTitleToEdit, setJobTitleToEdit] = useState<{ id: number | undefined; label: string }>(
    { id: undefined, label: '' }
  );
  const [error, setError] = useState('');

  const openEditModal = (id: number | undefined, label: string) => {
    setError('');
    setJobTitleToEdit({ id, label });
    setModalOpen(true);
  };

  const createJobTitle = async (jobTitle: JobTitle) => {
    try {
      const res = await post('/job_titles', { jobTitle: { label: jobTitle.label, jobTitleableId, jobTitleableType } });
      if (res.id !== undefined) {
        setCurrentJobTitles([...currentJobTitles, res]);
        closeModal();
      } else {
        setError(res[0]);
      }
      return res.id;
    } catch {
      setError('Job Title could not be created.');
    }
  };

  const updateJobTitle = async (jobTitle) => {
    try {
      const res = await put(`/job_titles/${jobTitle.id}`, { jobTitle: { label: jobTitle.label } });
      if (res.id !== undefined) {
        setCurrentJobTitles(currentJobTitles.map(jt => jt.id === res.id ? res : jt));
        closeModal();
      } else {
        setError(res[0]);
      }
    } catch {
      setError('Job Title could not be created.');
    }
  };

  const deleteJobTitle = async (jobTitleId) => {
    setError('');
    try {
      await deleteRequest(`/job_titles/${jobTitleId}`);
      setCurrentJobTitles(currentJobTitles.filter(jt => jt.id !== jobTitleId));
    } catch {
      setError('Job Title could not be deleted.');
      return null;
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setError('');
    setJobTitleToEdit({ id: undefined, label: '' });
  };

  return (
    <React.Fragment>
      <Modal
        show={modalOpen}
        onHide={closeModal}
      >
        <JobTitleModal
          closeModal={closeModal}
          error={error}
          id={jobTitleToEdit.id}
          label={jobTitleToEdit.label}
          submitForm={!jobTitleToEdit.id ? createJobTitle : updateJobTitle}
        />
      </Modal>
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <h6>Job Titles</h6>
        <button className="btn btn-primary" onClick={() => openEditModal(undefined, '')}>Add Job Title</button>
      </div>
      <ErrorAlert error={error}/>

      {currentJobTitles.length > 0 && (
        <div className="table-container">
          <table className="table">
            <tbody>
              {currentJobTitles.map(jobTitle => (
                <tr className="align-middle" key={jobTitle.id}>
                  <td className="w-100"><strong>{jobTitle.label}</strong></td>
                  <td>
                    <div className="d-flex justify-content-end gap-2 flex-nowrap">
                      <Secondary icon={faPencil} plain type="button" onClick={() => openEditModal(jobTitle.id, jobTitle.label)}>
                        Edit
                      </Secondary>
                      <Danger plain onClick={() => deleteJobTitle(jobTitle.id)}>
                        <FontAwesomeIcon icon={faTrash}/>
                      </Danger>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </React.Fragment>
  );
}

type JobTitle = {
  id: number,
  label: string
};

type JobTitlesProps = {
  jobTitleableId: number,
  jobTitleableType: string,
  jobTitles: JobTitle[]
};
